import { useMemoizedFn } from 'ahooks';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Form,
  Input,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Upload,
} from 'tdesign-react';
import ListPanel from '@/components/ListPanel';
import PermWrapper from '@/components/PermWrapper';
import useTable from '@/hooks/useTable';
import {
  getComponentList,
  componentExport,
  uploadComponentFile,
  deleteComponent,
} from '@/services/templateService';
import { downloadFile, renderDateTime, renderOptions } from '@/utils/common';
import { clusterTypeOptions } from '@/utils/constant';

const { FormItem } = Form;

export default function List() {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  /**
   * @type {import('tdesign-react/lib/table/type').BaseTableCol}
   */
  const columns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      fixed: 'left',
      width: 46,
    },
    {
      colKey: 'name',
      title: '组件名称',
      fixed: 'left',
      width: 260,
      cell: ({ row }) => (
        <>
          {row.systemComponent && (
            <Tag style={{ marginRight: 6 }} size='small' theme='danger'>
              system
            </Tag>
          )}
          {row.name}
        </>
      ),
    },
    {
      colKey: 'type',
      title: '组件类型',
      cell: renderOptions,
      options: clusterTypeOptions,
      width: 120,
    },
    {
      colKey: 'note',
      title: '组件作用说明',
      width: 280,
      ellipsis: true,
    },
    {
      title: '创建人',
      colKey: 'createBy',
      width: 150,
    },
    {
      title: '最后修改人',
      colKey: 'updateBy',
      width: 150,
    },
    {
      title: '创建时间',
      colKey: 'createTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='pulsar:stencil:component:update'>
            <Link
              theme='primary'
              onClick={() => navigate(`/maintenance/template/component/detail/${row.id}`)}
              hover='color'
            >
              编辑
            </Link>
          </PermWrapper>
          {!row.systemComponent && (
            <PermWrapper resource='pulsar:stencil:component:delete'>
              <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
                <Link theme='danger' hover='color'>
                  删除
                </Link>
              </Popconfirm>
            </PermWrapper>
          )}
        </Space>
      ),
      width: 120,
    },
  ];
  const loadData = useMemoizedFn(async (params) => {
    setSelectedRowKeys([]);
    const { data } = await getComponentList(params);
    setData(data.items, data.count);
  });

  const onSelectChange = (value) => {
    setSelectedRowKeys(value);
  };

  const download = async () => {
    const { data } = await componentExport({
      idList: selectedRowKeys,
    });
    downloadFile(new Blob([data], { type: 'application/json;charset=utf-8' }), '组件模板_');
  };

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    search: (
      <FormItem name='name'>
        <Input clearable placeholder='组件名称' />
      </FormItem>
    ),
    operators: (
      <PermWrapper resource='pulsar:stencil:component:create'>
        <Button
          theme='primary'
          onClick={() => {
            navigate('/maintenance/template/component/detail');
          }}
        >
          创建组件
        </Button>
      </PermWrapper>
    ),
    remote: true,
    onSearch: loadData,
  });

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const doDelete = async ({ id }) => {
    try {
      await deleteComponent(id);
      MessagePlugin.success('删除成功');
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    }
  };

  const doUpload = async (file) => {
    if (file[0]) {
      const formData = new FormData();
      formData.append('file', file[0]);
      let uploadMsg;
      try {
        uploadMsg = MessagePlugin.loading({
          duration: 0,
          content: '正在上传...',
        });
        await uploadComponentFile(formData);
        MessagePlugin.success('上传成功！');
        doRefresh();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        MessagePlugin.close(uploadMsg);
      }
    }
  };

  return (
    <ListPanel
      header='组件模板'
      tip='组件标识pulsar集群的一个组件，在实际运行的机器有独立的部署目录和独立的服务进程，例如 元数据zookeeper，数据存储bookie等。组件管理主要管理物料包、启停脚本和变量信息等'
      operators={
        <Space>
          <PermWrapper resource='pulsar:stencil:component:export'>
            <Tooltip content={isEmpty(selectedRowKeys) ? '请先勾选要导出的组件' : null}>
              <BigButton
                theme='default'
                variant='outline'
                onClick={download}
                size='large'
                disabled={isEmpty(selectedRowKeys)}
              >
                批量导出
              </BigButton>
            </Tooltip>
          </PermWrapper>
          <PermWrapper resource='pulsar:stencil:component:import'>
            <Upload theme='file' onSelectChange={doUpload}>
              <BigButton theme='default' variant='outline' size='large'>
                导入JSON文件
              </BigButton>
            </Upload>
          </PermWrapper>
        </Space>
      }
    >
      {header}
      <Table
        {...config}
        rowKey='id'
        selectedRowKeys={selectedRowKeys}
        onSelectChange={onSelectChange}
      />
    </ListPanel>
  );
}

const BigButton = styled(Button)`
  border: none;
  &&& {
    font: var(--td-font-body-medium);
  }
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1);
`;
